/**
 * ChatListView - List of all active chats
 * Shows bookings with their last message and unread count
 */

import { Calendar, MapPin, User, HeadphonesIcon } from 'lucide-react'
import { useChat } from '../../contexts/ChatContext'
import { useAuth } from '../../contexts/AuthContext'
import { formatDistanceToNow } from 'date-fns'
import { getFirstNameOnly } from '../../lib/privacy/sanitizeTalentData'

interface ChatListViewProps {
  onSelectBooking: (bookingId: string, threadId?: string | null) => void
  onSelectAdminChat?: () => void
}

export default function ChatListView({ onSelectBooking, onSelectAdminChat }: ChatListViewProps) {
  const { activeChats, openChat, isLoading } = useChat()
  const { profile } = useAuth()

  const handleChatClick = (chat: any) => {
    if (chat.type === 'admin') {
      // Admin support chat
      if (onSelectAdminChat) {
        onSelectAdminChat()
      }
    } else {
      // Booking chat. Switch the view first so the conversation opens even if remembering
      // the selection fails; openChat only records state and cannot throw anymore, but the
      // order still expresses the priority: open now, remember second.
      onSelectBooking(chat.bookingId, chat.threadId)
      openChat(chat.bookingId, chat.threadId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (activeChats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4 text-gray-500">
        <User className="w-12 h-12 mb-3 text-gray-300" />
        <p className="font-medium">No active chats</p>
        <p className="text-sm mt-1">Chats become available 30 days before your wedding</p>
      </div>
    )
  }

  return (
    <div className="overflow-y-auto h-full">
      {activeChats.map((chat) => {
        const lastMessageTime = chat.lastMessageAt
          ? formatDistanceToNow(new Date(chat.lastMessageAt), { addSuffix: true })
          : 'No messages yet'

        // Admin support chat
        if (chat.type === 'admin') {
          return (
            <button
              key="admin-support"
              onClick={() => handleChatClick(chat)}
              className="w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left focus:outline-none focus:bg-gray-50 bg-blue-50/50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <HeadphonesIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="font-medium text-gray-900">Support Team</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Chat with our support team
                  </div>
                  <div className="text-sm text-gray-400 italic truncate">
                    {lastMessageTime}
                  </div>
                </div>

                {/* Unread badge */}
                {chat.unreadCount > 0 && (
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center bg-blue-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5">
                      {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
                    </span>
                  </div>
                )}
              </div>
            </button>
          )
        }

        // Booking chat
        const eventDate = chat.eventDate ? new Date(chat.eventDate) : null

        // Determine which name to show based on user role
        // Photographers see customer name, customers see photographer name
        const isPhotographer = profile?.role === 'photographer'
        const talentNames = [
          chat.photographer?.full_name,
          chat.videographer?.full_name,
          chat.secondPhotographer?.full_name
        ].filter(Boolean).map((name) => getFirstNameOnly(name))
        const displayName = isPhotographer
          ? (chat.customer?.full_name
              ? getFirstNameOnly(chat.customer.full_name)
              : chat.customer?.email
                ? chat.customer.email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
                : 'Client')
          : (talentNames.length > 0 ? talentNames.join(' + ') : 'Wedding Team')

        return (
          <button
            key={chat.threadId || chat.bookingId}
            onClick={() => handleChatClick(chat)}
            className="w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left focus:outline-none focus:bg-gray-50"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                {/* Contact name - shows the OTHER person in the conversation */}
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="font-medium text-gray-900 truncate">
                    {displayName}
                  </span>
                </div>

                {/* Event type - SECONDARY */}
                <div className="text-sm text-gray-600 mb-1">
                  {chat.eventType || 'Wedding'}
                </div>

                {/* Event date */}
                {eventDate && (
                  <div className="text-sm text-gray-500 mb-2">
                    <Calendar className="w-3 h-3 inline mr-1 flex-shrink-0" />
                    {eventDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                )}

                {/* Venue */}
                {chat.venueName && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{chat.venueName}</span>
                  </div>
                )}

                {/* Last message preview */}
                <div className="text-sm text-gray-400 italic truncate">
                  {lastMessageTime}
                </div>
              </div>

              {/* Unread badge */}
              {chat.unreadCount > 0 && (
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center justify-center bg-primary text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5">
                    {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
                  </span>
                </div>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
